<template>
    <ContentComp>
        <FormLayout>
            <FormLayoutRow>
                <FormLayoutLabel
                    text="import.club"
                    v-if="!hideLabel"
                />

                <DropdownSelect
                    class="flex-fill w-min-300"
                    :show-search="true"
                    :loadstate="GetIsonenItemsLoadState"
                    :data="isonenDropdownElements"
                    v-model="selectedIsonenItem"
                    search-type="ContainsAll"
                />
            </FormLayoutRow>
            <FormLayoutRow v-if="availableExternalDatasourceEvents !== null">
                <FormLayoutLabel
                    text="eventwizard.event"
                    v-if="!hideLabel"
                />

                <DropdownSelect
                    class="flex-fill w-min-300"
                    :data="isonenEventsDropdownElements"
                    v-model="selectedExternalDatasourceEventKey"
                    caption="externaleventsource.selectevent"
                    :loadstate="FetchIsonenEventLoadState"
                    :show-item-count-badge="true"
                    search-type="ContainsAll"
                    :disabled="selectedIsonenItem === null"
                />
            </FormLayoutRow>
            <FormLayoutRow v-if="externalEventsLoadFailed || currentSelectedEventFailed">
                <FormLayoutLabel v-if="!hideLabel" />

                <AlertMessage
                    class="w-100 flex-fill"
                    innerclass="flex-fill"
                    size="sm"
                    type="danger"
                >
                    <i18next
                        :translation="$t('externaleventsource.eventsloadfailed')"
                        v-if="externalEventsLoadFailed"
                    >
                        <template #action>
                            <a
                                href="#"
                                @click="fetchEventsForCurrentItem"
                                >{{ $tm('general.click') }}</a
                            >
                        </template>
                    </i18next>
                    <i18next
                        :translation="$t('externaleventsource.currentselectedeventloadfailed')"
                        v-else
                    >
                        <template #action>
                            <a
                                href="#"
                                @click="fetchDetailsForCurrentItem"
                                >{{ $tm('general.click') }}</a
                            >
                        </template>
                    </i18next>
                </AlertMessage>
            </FormLayoutRow>

            <!-- We set this check to 2 since there is always an NULL option -->
            <FormLayoutRow v-if="availableEventsAsSelectLine?.length > 2">
                <FormLayoutLabel
                    text="eventwizard.selectday"
                    v-if="!hideLabel"
                />

                <DropdownSelect
                    class="flex-fill"
                    :data="availableEventsAsSelectLine"
                    v-model="selectedEvent"
                />
            </FormLayoutRow>
        </FormLayout>
    </ContentComp>
</template>
<script setup lang="ts">
import { AlertMessage, ContentComp, DropdownSelect } from '@/components';
import { FormLayoutRow } from '@/components/Form';
import FormLayout from '@/components/Form/FormLayout.vue';
import FormLayoutLabel from '@/components/Form/FormLayoutLabel.vue';
import { useTaskLoader } from '@/composeables';
import { ExternalEventDatasourceDao } from '@/dao';
import type { SelectLine, SelectLineValueType } from '@/objects';
import { ExternalDataSource, ExternalDatasourceEvent, ExternalEventDatasourceResponse, ISonenItem } from '@/objects/generated';
import { computed, onMounted, ref, watch } from 'vue';
import { formatDateDefault } from '@/utilities/dateutils';
import { $tm } from '@/locize';

/**
 * This will get a list of the logged in users external datasources
 *
 * NOTE! For now it is hardcoded to show isonen.
 *
 * TODO: I am thinking a datastructue in the backend that implements an IExternalEventSource interface wich contains details if is is filebased or API based and what kind of auth. and probably
 * some default settings for races etc.
 *
 * This component should return the neccessary data to create a new event.
 */

const props = withDefaults(
    defineProps<{
        preselectClub?: boolean;

        /**
         * Hide the label for the dropdowns
         * @default false
         */
        hideLabel?: boolean;
    }>(),
    {
        hideLabel: false,
        preselectClub: true,
    },
);

/**
 * Define the emits for this component
 */
const emit = defineEmits<{
    /**
     * Emitted when a club/item is selected from the dropdown
     * @param {string} e
     */
    (e: 'clubSelected', club: ISonenItem): void;
}>();

// The currently selected item from the external datasource
const selectedIsonenItem = ref<SelectLineValueType | null>(null);

/**
 * Contains a list of all the events/items for the selected datasource
 */
const modelList = defineModel<ExternalEventDatasourceResponse[]>({ required: true });

/**
 * The actual selected model for a given day
 */
const selectedValue = defineModel<ExternalEventDatasourceResponse | null>('event', { required: false });

const selectedEvent = ref<string | null>(null);

watch(selectedEvent, (nv) => {
    if (!nv) {
        selectedValue.value = null;
        return;
    }

    const found = modelList.value.find((x) => x.EventInfo!.Name === nv);
    selectedValue.value = found || null;
});

// The loader and state for fetching the isonen items
const { AddLoader: GetIsonenItemsAddLoader, LoadState: GetIsonenItemsLoadState } = useTaskLoader();

// The loader and state for fetching the isonen items
const { AddLoader: FetchIsonenEventAddLoader, LoadState: FetchIsonenEventLoadState } = useTaskLoader();

// The loader and state for handling the isonen event data
const { AddLoader: HandleIsonenEventAddLoader, LoadState: HandleIsonenEventLoadState } = useTaskLoader();

/**
 * A list of the externals
 */
const externalDatasources = ref<ExternalDataSource[]>([]);

/**
 * If the load of the external events failed
 */
const externalEventsLoadFailed = ref<boolean>(false);

/**
 * Is used for showing an error message if loading the currently selected event failed
 */
const currentSelectedEventFailed = ref<boolean>(false);

// Add isonen
externalDatasources.value.push(
    ExternalDataSource.FromJsValues({
        Name: 'ISonen',
        Type: 'FileDrop',
        LogoSource: 'https://cdn-assets.productfruits.com/fefd9ef6-0392-4624-9df9-269985775404',
    }),
);

// Contains the list of all the isonen clubs/items we can fetch data from
const isonenItems = ref<ISonenItem[]>([]);

/**
 * Contains the selected item from isonen to use in the dropdown
 */
const isonenDropdownElements = computed(() => {
    const collator = new Intl.Collator('nb');

    const items = isonenItems.value
        .map((item) => {
            return {
                value: item.APIID,
                text: item.DescriptiveName,
            } as SelectLine;
        })
        .sort((a, b) => collator.compare(a.text, b.text));

    // We add a null selector first
    items.unshift({
        value: null,
        text: 'isonenselector.selectclub',
    } as SelectLine);

    return items;
});

// Contains a list of all the available events from the selected datasource and club/item
const availableExternalDatasourceEvents = ref<ExternalDatasourceEvent[]>([]);

const isonenEventsDropdownElements = computed(() => {
    if (!availableExternalDatasourceEvents.value) {
        return [];
    }
    return availableExternalDatasourceEvents.value.map((item) => {
        return {
            value: item.Key,
            text: formatEventName(item),
            subtext: formatDateDefault(item.Date, 'DATE'),
        } as SelectLine;
    });
});

const selectedExternalDatasourceEventKey = ref<string | null>(null);

/**
 * Return a formatted name for the event
 * The name contains the name, date and location (if available)
 * @param {ExternalDatasourceEvent} event
 */
const formatEventName = (event: ExternalDatasourceEvent) => {
    const location = event.Location ? `(${event.Location})` : '';

    return `${event.Name} ${location}`.trim();
};
/**
 * Currently selected isonen item
 */
const currentIsonenItem = ref<ISonenItem | null>(null);

/**
 * Fetch all the events/items for the currently selected item.
 */
const fetchEventsForCurrentItem = async () => {
    externalEventsLoadFailed.value = false;

    if (currentIsonenItem.value === null) {
        availableExternalDatasourceEvents.value = [];
        return;
    }

    await FetchIsonenEventAddLoader(async () => {
        try {
            let extEvents = await ExternalEventDatasourceDao.GetDatasourceEvents(currentIsonenItem.value!, '');

            // We should order these by date desc and then move the one with the next date to the top
            extEvents = extEvents.sort((a, b) => b.Date.getTime() - a.Date.getTime());

            let nextEventIndex = -1;
            for (let i = 0; i < extEvents.length; i++) {
                const ev = extEvents[i];
                // As soon as it is older than today we stop
                if (ev.Date < new Date()) {
                    break;
                }
                nextEventIndex = i;
            }

            if (nextEventIndex !== -1) {
                // Remove from the original position and add to the top
                let nextItem = extEvents.splice(nextEventIndex, 1)[0];
                extEvents.unshift(nextItem);
            }

            availableExternalDatasourceEvents.value = extEvents;
        } catch (e) {
            externalEventsLoadFailed.value = true;
        }
    }, '');
};

/**
 * Will load the details for the currently selected event
 */
const fetchDetailsForCurrentItem = async () => {
    if (!currentIsonenItem.value || !selectedExternalDatasourceEventKey.value) {
        return;
    }

    selectedEvent.value = null;
    selectedValue.value = null;

    currentSelectedEventFailed.value = false;
    setTimeout(async () => {
        await FetchIsonenEventAddLoader(async () => {
            try {
                // TODO: We need to load the ExternalEventDatasourceResponse for the given event item
                modelList.value = await ExternalEventDatasourceDao.GetEventDatasourceResponse(
                    currentIsonenItem.value!,
                    selectedExternalDatasourceEventKey.value,
                );
                if (modelList.value.length === 0) {
                    return;
                }

                // If there is only one event we preselect that
            } catch (e) {
                // We need to show a error message saying we could not load the event from isonen
                console.error(e);
                currentSelectedEventFailed.value = true;
            }
        }, '');
    }, 100);
};

const availableEventsAsSelectLine = computed(() => {
    if (!modelList.value) {
        return [];
    }
    const values = modelList.value.map((item) => {
        return {
            value: item.EventInfo!.Name || '',
            text: `${item.EventInfo!.Name || ''} (${$tm('externaldata.numberofcontestants', item.Contestants.length)})`,
            subtext: formatDateDefault(item.EventInfo!.Date, 'DATE'),
        } as SelectLine;
    });

    // Add a empty options first
    values.unshift({
        value: null,
        text: 'externaleventsource.selectday',
        separator: true,
    } as SelectLine);

    return values;
});

/**
 * We watch for changes on the selected event key and then load the data for that event
 */
watch(selectedExternalDatasourceEventKey, async () => await fetchDetailsForCurrentItem());

/**
 * This listens for changes for the selected isonen item and get a list of the events for that item
 */
watch(selectedIsonenItem, async (nv) => {
    currentIsonenItem.value = isonenItems.value?.find((x) => x.APIID === (nv as number)) ?? null;
    fetchEventsForCurrentItem();

    emit('clubSelected', currentIsonenItem.value!);
});

watch(modelList, (nv) => {
    // if there is only one event we automatically select that
    if (nv && nv.length === 1) {
        selectedEvent.value = nv[0].EventInfo!.Name || null;

        console.log('Auto selecting event:', selectedEvent.value);
    }
});

onMounted(async () => {
    await GetIsonenItemsAddLoader(async () => {
        isonenItems.value = await ExternalEventDatasourceDao.IsonenItemsForUser();

        // We preselect the first item
        if (props.preselectClub && isonenItems.value && isonenItems.value.length > 0) {
            selectedIsonenItem.value = isonenItems.value[0].APIID;
        }
    }, '');
});
</script>
